(() => {
  if (window.acaseCartSync) return;
  const nativeFetch = window.fetch.bind(window);
  const payloadRevisions = new WeakMap();
  const keyAliases = new Map();
  const desired = new Map();
  const giftActions = new Set();
  let actionId = 0;
  let tail = Promise.resolve();
  let pending = 0;
  let revision = 0;
  let failures = 0;
  let healthy = true;
  let refreshing = false;
  let checkingOut = false;

  const errorText = () => window.cartStrings?.error || 'Unable to update your cart. Please try again.';
  const cartUrl = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  const endpoint = (action) => window.routes?.[`cart_${action}_url`] || `${cartUrl()}/${action}.js`;
  const roots = () => document.querySelectorAll('cart-items, cart-drawer-items');
  const rowInput = (row) => row?.querySelector('input[name="updates[]"]');
  const nextTurn = () => new Promise((resolve) => setTimeout(resolve, 0));

  function busy() {
    roots().forEach((root) => root.setAttribute('aria-busy', String(pending > 0)));
    document.querySelectorAll('.cart__dynamic-checkout-buttons, #main-cart-footer .additional-checkout-buttons').forEach((node) => {
      if (pending && !node.hasAttribute('data-acase-inert')) {
        node.dataset.acaseInert = String(node.inert);
        node.inert = true;
      } else if (!pending && node.hasAttribute('data-acase-inert')) {
        node.inert = node.dataset.acaseInert === 'true';
        delete node.dataset.acaseInert;
      }
    });
  }

  function enqueue(operation) {
    pending += 1;
    busy();
    const result = tail.then(operation);
    tail = result.catch(() => {}).finally(() => {
      pending -= 1;
      busy();
    });
    return result;
  }

  async function idle() {
    do {
      const current = tail;
      await current;
      await nextTurn();
      if (current === tail && pending === 0) return;
    } while (true);
  }

  function showError(message, key) {
    document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((node) => {
      node.textContent = message || '';
    });
    if (key) {
      document.querySelectorAll('.cart-item').forEach((row) => {
        if (row.dataset.cartKey === key) {
          const node = row.querySelector('.cart-item__error-text');
          if (node) node.textContent = message || '';
        }
      });
    }
  }

  function isMutation(input, options) {
    try {
      const url = new URL(input instanceof Request ? input.url : String(input), location.href);
      const method = options?.method || (input instanceof Request ? input.method : 'GET');
      return url.origin === location.origin && method.toUpperCase() === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch {
      return false;
    }
  }

  window.fetch = (input, options) => {
    if (!isMutation(input, options)) return nativeFetch(input, options);
    return new Promise((resolve, reject) => {
      enqueue(async () => {
        try {
          const response = await nativeFetch(input, options);
          const ticket = ++revision;
          await new Promise((release) => {
            let consumed = false;
            let released = false;
            const finish = () => {
              if (!released) {
                released = true;
                release();
              }
            };
            for (const method of ['json', 'text', 'arrayBuffer', 'blob', 'formData']) {
              const read = response[method].bind(response);
              response[method] = async () => {
                consumed = true;
                try {
                  const value = await read();
                  if (method === 'json' && value && typeof value === 'object') {
                    payloadRevisions.set(value, ticket);
                    if (response.ok && (value.errors || value.status >= 400)) failures += 1;
                  }
                  return value;
                } finally {
                  setTimeout(finish, 0);
                }
              };
            }
            response.clone().text().then(() => {
              setTimeout(() => { if (!consumed) finish(); }, 0);
            }, () => { setTimeout(() => { if (!consumed) finish(); }, 0); });
            if (!response.ok) failures += 1;
            resolve(response);
          });
        } catch (error) {
          failures += 1;
          healthy = false;
          requestRefresh();
          reject(error);
        }
      });
    });
  };

  function sections() {
    const result = [];
    if (document.querySelector('cart-drawer')) result.push({ id: 'cart-drawer', target: '#CartDrawer', selector: '#CartDrawer' });
    if (document.getElementById('cart-icon-bubble')) result.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', selector: '.shopify-section' });
    for (const name of ['main-cart-items', 'main-cart-footer']) {
      const node = document.getElementById(name);
      if (node) result.push({ id: node.dataset.id || name, target: `#${name} .js-contents`, selector: `#${name} .js-contents` });
    }
    if (document.getElementById('cart-live-region-text')) result.push({ id: 'cart-live-region-text', target: '#cart-live-region-text', selector: '.shopify-section' });
    return result;
  }

  function properties(value) {
    if (Array.isArray(value)) value = Object.fromEntries(value);
    return Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b));
  }

  function identity(item) {
    return JSON.stringify([String(item.variant_id || item.id), properties(item.properties), String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  }

  function inputIdentity(input) {
    try {
      return identity({ variant_id: input.dataset.quantityVariantId, properties: JSON.parse(input.dataset.cartProperties || '{}'), selling_plan_allocation: { selling_plan: { id: input.dataset.cartSellingPlan } } });
    } catch {
      return '';
    }
  }

  function rememberKeys(cart) {
    if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number') return;
    const current = new Map();
    cart.items.forEach((item) => {
      const fingerprint = identity(item);
      current.set(fingerprint, [...(current.get(fingerprint) || []), item.key]);
    });
    document.querySelectorAll('input[name="updates[]"][data-cart-key]').forEach((input) => {
      const key = input.dataset.cartKey;
      if (cart.items.some((item) => item.key === key)) return;
      const matches = current.get(inputIdentity(input)) || [];
      keyAliases.set(key, matches.length === 1 ? matches[0] : null);
    });
  }

  function currentKey(key) {
    const seen = new Set();
    while (keyAliases.has(key) && !seen.has(key)) {
      seen.add(key);
      key = keyAliases.get(key);
      if (!key) return null;
    }
    return key;
  }

  function preserve(oldRoot, newRoot) {
    const selectors = 'input[name="cart-discount-field"], textarea[name="note"], [name^="attributes["]';
    oldRoot.querySelectorAll(selectors).forEach((old) => {
      const fresh = Array.from(newRoot.querySelectorAll(selectors)).find((node) => node.name === old.name);
      if (!fresh) return;
      fresh.value = old.value;
      if (fresh.tagName === 'TEXTAREA') fresh.textContent = old.value;
      else fresh.setAttribute('value', old.value);
      if (old.type === 'checkbox' || old.type === 'radio') {
        fresh.checked = old.checked;
        fresh.toggleAttribute('checked', old.checked);
      }
    });
    oldRoot.querySelectorAll('details[open]').forEach((old) => {
      const fresh = Array.from(newRoot.querySelectorAll('details')).find((node) => node.id === old.id);
      if (fresh) fresh.setAttribute('open', '');
    });
    const oldTimer = oldRoot.querySelector('.cart-timer');
    const newTimer = newRoot.querySelector('.cart-timer');
    if (oldTimer && newTimer) newTimer.innerHTML = oldTimer.innerHTML;
    const extras = 'cart-drawer-upsell[data-toggle="true"], cart-drawer-gift';
    const previous = Array.from(oldRoot.querySelectorAll(extras));
    newRoot.querySelectorAll(extras).forEach((fresh) => {
      const old = previous.find((node) => node.tagName === fresh.tagName && node.dataset.handle === fresh.dataset.handle);
      if (!old) return;
      if (fresh.matches('cart-drawer-upsell')) fresh.dataset.selected = old.dataset.selected;
      fresh.dataset.id = old.dataset.id;
      const oldId = old.querySelector('[name="id"]');
      const newId = fresh.querySelector('[name="id"]');
      if (oldId && newId) newId.setAttribute('value', oldId.value);
      const oldImage = old.querySelector('.upsell__image__img');
      const newImage = fresh.querySelector('.upsell__image__img');
      if (oldImage && newImage) newImage.setAttribute('src', oldImage.getAttribute('src'));
      const oldOptions = old.querySelectorAll('.select__select');
      fresh.querySelectorAll('.select__select').forEach((select, index) => {
        if (!oldOptions[index]) return;
        select.querySelectorAll('option').forEach((option) => option.toggleAttribute('selected', option.value === oldOptions[index].value));
      });
    });
  }

  function render(cart, requested = sections()) {
    const fragments = requested.map((section) => {
      const target = document.querySelector(section.target);
      const html = cart.sections?.[section.id];
      if (!target) return null;
      if (typeof html !== 'string') throw new Error('Missing cart section');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(section.selector);
      if (!fresh) throw new Error('Invalid cart section');
      preserve(target, fresh);
      return { target, fresh, section, doc };
    }).filter(Boolean);
    const active = document.activeElement;
    const focusKey = active?.closest('.cart-item')?.dataset.cartKey;
    const focusName = active?.getAttribute('name');
    const focusedCart = active?.closest('cart-drawer') || active?.closest('cart-items');
    rememberKeys(cart);
    fragments.forEach(({ fresh }) => {
      fresh.querySelectorAll('input[name="updates[]"][data-cart-key]').forEach((input) => keyAliases.delete(input.dataset.cartKey));
    });
    fragments.forEach(({ target, fresh }) => { target.innerHTML = fresh.innerHTML; });
    const drawer = document.querySelector('cart-drawer');
    const drawerFragment = fragments.find(({ section }) => section.id === 'cart-drawer');
    const empty = typeof cart.item_count === 'number' ? cart.item_count === 0 : drawerFragment?.doc.querySelector('cart-drawer')?.classList.contains('is-empty');
    if (typeof empty === 'boolean') {
      roots().forEach((root) => root.classList.toggle('is-empty', empty));
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
      drawer?.classList.toggle('is-empty', empty);
      document.querySelectorAll('button[name="checkout"][form="cart"], button[name="checkout"][form="CartDrawer-Form"]').forEach((button) => { button.disabled = empty; });
    }
    if (drawer) {
      drawer.upsellHandles = drawer.getUpsellHandles();
      drawer.checkForClear();
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      if (drawer.classList.contains('active')) drawer.querySelector('countdown-timer')?.playTimer();
      drawer.querySelectorAll('cart-drawer-gift').forEach((gift) => {
        const present = Array.from(drawer.querySelectorAll('.cart-item')).some((row) => row.classList.contains(`cart-item--product-${gift.dataset.handle}`));
        const action = present && gift.dataset.selected === 'false' ? 'removeFromCart' : (!present && gift.dataset.selected === 'true' && !empty ? 'addToCart' : null);
        const key = `${gift.dataset.handle}:${gift.dataset.id}:${action}`;
        if (action && !giftActions.has(key)) {
          giftActions.add(key);
          try { gift[action](); } finally { idle().then(() => giftActions.delete(key)); }
        }
      });
    }
    if (focusedCart && focusedCart.isConnected) {
      const key = currentKey(focusKey);
      const row = Array.from(focusedCart.querySelectorAll('.cart-item')).find((node) => node.dataset.cartKey === key);
      const focus = Array.from(row?.querySelectorAll('[name]') || []).find((node) => node.name === focusName)
        || focusedCart.querySelector('.cart-item__name, .drawer__close, .cart__warnings a');
      if (drawer?.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), focus || drawer.querySelector('.drawer__close'));
      else focus?.focus({ preventScroll: true });
    }
    healthy = true;
    busy();
  }

  async function readJson(url, options) {
    const response = await nativeFetch(url, options);
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) {
      const error = new Error(typeof data.errors === 'string' ? data.errors : data.description || errorText());
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function refresh() {
    const requested = sections();
    const url = new URL(cartUrl(), location.origin);
    url.searchParams.set('sections', requested.map((section) => section.id).join(','));
    const [cart, rendered] = await Promise.all([
      readJson(`${cartUrl()}.js`, { cache: 'no-store' }),
      readJson(url.href, { cache: 'no-store' })
    ]);
    cart.sections = rendered;
    render(cart, requested);
    return cart;
  }

  function requestRefresh() {
    if (refreshing) return tail;
    refreshing = true;
    return enqueue(async () => {
      try {
        return await refresh();
      } catch {
        healthy = false;
        failures += 1;
        showError(errorText());
      } finally {
        refreshing = false;
      }
    });
  }

  function lineKey(owner, line) {
    if (owner && !owner.isConnected) owner = document.querySelector(owner.tagName.toLowerCase());
    const input = owner?.querySelector(`input[name="updates[]"][data-index="${Number(line)}"]`);
    return input?.dataset.cartKey;
  }

  function change(key, quantity, clear = false, allowClear = false) {
    if ((!key && !clear) || !Number.isInteger(quantity) || quantity < 0) return Promise.resolve();
    showError('');
    const action = ++actionId;
    if (key) desired.set(key, { quantity, action });
    return enqueue(async () => {
      const resolvedKey = currentKey(key);
      if (!clear && !resolvedKey) {
        if (desired.get(key)?.action === action) desired.delete(key);
        return;
      }
      if (allowClear && quantity === 0) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          const handles = drawer.getUpsellHandles();
          const normal = Array.from(drawer.querySelectorAll('.cart-item')).filter((row) => !handles.some((handle) => row.classList.contains(`cart-item--product-${handle}`)));
          clear = normal.length === 1 && normal[0].dataset.cartKey === resolvedKey;
        }
      }
      const requested = sections();
      const body = { sections: requested.map((section) => section.id), sections_url: location.pathname };
      if (!clear) Object.assign(body, { id: resolvedKey, quantity });
      try {
        const options = () => ({ method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        let cart;
        try {
          cart = await readJson(endpoint(clear ? 'clear' : 'change'), options());
        } catch (error) {
          if (clear || ![400, 404].includes(error.status)) throw error;
          await refresh();
          const freshKey = currentKey(key);
          if (!freshKey || freshKey === body.id) throw error;
          body.id = freshKey;
          cart = await readJson(endpoint('change'), options());
        }
        revision += 1;
        if (desired.get(key)?.action === action) desired.delete(key);
        try {
          render(cart, requested);
        } catch {
          await refresh();
        }
        const item = cart.items?.find((item) => item.key === currentKey(key));
        if (!clear && quantity > 0 && item && item.quantity !== quantity) {
          showError(window.cartStrings?.quantityError?.replace('[quantity]', item.quantity) || errorText(), item.key);
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
      } catch (error) {
        failures += 1;
        if (desired.get(key)?.action === action) desired.delete(key);
        try { await refresh(); } catch { healthy = false; }
        showError(error.message || errorText(), currentKey(key));
      }
    });
  }

  async function checkoutAfterUpdates(navigate) {
    if (checkingOut) return;
    checkingOut = true;
    const startFailures = failures;
    const buttons = document.querySelectorAll('.cart__checkout-button');
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    try {
      await idle();
      if (!healthy || failures !== startFailures) {
        showError(errorText());
        return;
      }
      navigate();
    } finally {
      checkingOut = false;
      document.querySelectorAll('.cart__checkout-button').forEach((button) => button.removeAttribute('aria-busy'));
    }
  }

  const CartItemsClass = customElements.get('cart-items');
  if (CartItemsClass) {
    CartItemsClass.prototype.updateQuantity = function (line, quantity) { return change(lineKey(this, line), Number(quantity)); };
    CartItemsClass.prototype.clearCart = function () { return change(null, 0, true); };
    CartItemsClass.prototype.onCartUpdate = requestRefresh;
    CartItemsClass.prototype.onChange = function (event) {
      if (event.target.matches('input[name="updates[]"]')) return change(event.target.dataset.cartKey, Number(event.target.value));
    };
  }
  const DrawerClass = customElements.get('cart-drawer');
  if (DrawerClass) {
    DrawerClass.prototype.checkForClear = function () {
      const handles = this.getUpsellHandles();
      const normal = Array.from(this.querySelectorAll('.cart-item')).filter((row) => !handles.some((handle) => row.classList.contains(`cart-item--product-${handle}`)));
      this.querySelectorAll('cart-remove-button').forEach((button) => { button.clearCart = normal.length <= 1 && normal.includes(button.closest('.cart-item')); });
    };
    DrawerClass.prototype.renderContents = function (data, skipOpen = false) {
      const ticket = payloadRevisions.get(data);
      const requested = sections().filter((section) => data.sections?.[section.id] !== undefined);
      if (ticket !== undefined && ticket < revision) requestRefresh();
      else if (!requested.length) requestRefresh();
      else {
        try { render(data, requested); } catch { requestRefresh(); }
      }
      if (!skipOpen) this.open();
    };
    document.querySelector('cart-drawer')?.checkForClear();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (link && link.closest('cart-drawer, cart-items, #main-cart-footer')) {
      const url = new URL(link.href, location.href);
      if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        checkoutAfterUpdates(() => location.assign(url.href));
        return;
      }
    }
    const remove = target?.closest('cart-remove-button');
    const button = target?.closest('quantity-input button[name="minus"], quantity-input button[name="plus"]');
    const root = (remove || button)?.closest('cart-items, cart-drawer-items');
    if (!root) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (remove) {
      change(remove.dataset.cartKey || lineKey(root, remove.dataset.index), 0, false, true);
      return;
    }
    const input = button.closest('quantity-input').querySelector('input');
    const queued = Array.from(desired.entries()).filter(([key]) => currentKey(key) === input.dataset.cartKey).map(([, intent]) => intent).sort((a, b) => b.action - a.action)[0];
    const value = queued?.quantity ?? Number(input.value);
    const minimum = Math.max(1, Number(input.dataset.min || 1));
    const step = Math.max(1, Number(input.dataset.step || input.step || 1));
    const next = button.name === 'minus' ? (value - step < minimum ? 0 : value - step) : value + step;
    if (input.max && next > Number(input.max)) return;
    input.value = String(next);
    change(input.dataset.cartKey, next, false, next === 0);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[name="updates[]"]') || !input.closest('cart-items, cart-drawer-items')) return;
    event.stopImmediatePropagation();
    const quantity = Number(input.value);
    const minimum = Math.max(1, Number(input.dataset.min || 1));
    const step = Math.max(1, Number(input.dataset.step || input.step || 1));
    if (input.value.trim() === '' || !Number.isInteger(quantity) || quantity < 0 || (quantity > 0 && (quantity < minimum || (quantity - minimum) % step !== 0)) || (input.max && quantity > Number(input.max))) {
      input.value = input.defaultValue;
      showError(window.cartStrings?.quantityError?.replace('[quantity]', input.defaultValue) || errorText(), input.dataset.cartKey);
      return;
    }
    change(input.dataset.cartKey, quantity, false, quantity === 0);
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches('cart-note textarea[name="note"]')) {
      event.stopImmediatePropagation();
      const note = event.target.value;
      enqueue(async () => {
        try {
          await readJson(endpoint('update'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
        } catch {
          failures += 1;
          showError(errorText());
        }
      });
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('.cart-discount-form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const code = form.querySelector('[name="cart-discount-field"]')?.value.trim();
      const error = form.querySelector('.cart-discount-form__error');
      if (!code) {
        if (error) error.style.display = 'block';
        return;
      }
      if (error) error.style.display = 'none';
      const url = new URL(`${cartUrl().replace(/\/cart\/?$/, '')}/checkout`, location.origin);
      url.searchParams.set('discount', code);
      checkoutAfterUpdates(() => location.assign(url.href));
      return;
    }
    if (!['cart', 'CartDrawer-Form'].includes(form.id) || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutAfterUpdates(() => {
      const current = document.getElementById(form.id);
      if (!current || document.getElementById(event.submitter.id)?.disabled || current.closest('.is-empty')) return;
      const checkout = document.createElement('form');
      checkout.method = 'post';
      checkout.action = cartUrl();
      checkout.hidden = true;
      const entries = new FormData(current);
      entries.delete('checkout');
      entries.append('checkout', 'Checkout');
      for (const [name, value] of entries) {
        if (/^updates(?:\[|$)/.test(name) || typeof value !== 'string') continue;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        checkout.append(input);
      }
      document.body.append(checkout);
      HTMLFormElement.prototype.submit.call(checkout);
    });
  }, true);

  window.acaseCartSync = { whenIdle: idle, refresh: requestRefresh };
})();
